import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isFederatedUser } from '../../../services/cognitoService';
import { getUserProfile } from '../../../services/userService';

interface SettingsGeneralProps {
  successMessage: string;
  errorMessage: string;
  isLoading: boolean;
  onSave: (fields: { display_name: string; given_name: string; family_name: string }) => void;
  onCancel: () => void;
}

/** General settings tab — displays user profile info from backend DB */
const SettingsGeneral = ({
  successMessage,
  errorMessage,
  isLoading,
  onSave,
  onCancel,
}: SettingsGeneralProps) => {
  const { t } = useTranslation();
  const isFederated = isFederatedUser();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await getUserProfile();
        setEmail(profile.email ?? '');
        setDisplayName(profile.display_name ?? '');
        setGivenName(profile.given_name ?? '');
        setFamilyName(profile.family_name ?? '');
      } catch (_error) {
        // Fallback to localStorage if profile fetch fails
        setEmail(localStorage.getItem('userEmail') ?? '');
        setDisplayName(localStorage.getItem('displayName') ?? '');
      } finally {
        setProfileLoading(false);
      }
    };
    loadProfile();
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ display_name: displayName, given_name: givenName, family_name: familyName });
  };

  if (profileLoading) {
    return (
      <div className="settings-section">
        <h2 className="settings-section-title">{t('settings.profile', 'Profile')}</h2>
        <div className="usage-loading">
          <div className="spinner" />
          <span>{t('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">{t('settings.profile', 'Profile')}</h2>

      {/* Auth Provider Badge */}
      <div className="settings-auth-badge">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <title>Auth Provider Icon</title>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <span>
          {isFederated
            ? t('settings.authGoogle', 'Signed in with Google')
            : t('settings.authEmail', 'Signed in with Email')}
        </span>
      </div>

      {successMessage && (
        <div className="settings-message success">
          <span>{successMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="settings-message error">
          <span>{errorMessage}</span>
        </div>
      )}

      <form className="settings-form" onSubmit={handleSubmit}>
        {/* Email — read-only */}
        <div className="form-group">
          <label htmlFor="settings-email">{t('settings.email', 'Email')}</label>
          <input
            type="email"
            id="settings-email"
            value={email}
            readOnly
            className="form-input-readonly"
          />
          <span className="form-hint">{t('settings.emailHint', 'Email cannot be changed')}</span>
        </div>

        {/* Display Name */}
        <div className="form-group">
          <label htmlFor="settings-displayName">{t('settings.displayName', 'Display Name')}</label>
          <input
            type="text"
            id="settings-displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('settings.displayNamePlaceholder', 'Enter your display name')}
          />
        </div>

        {/* Given Name (First Name) */}
        <div className="form-group">
          <label htmlFor="settings-givenName">{t('settings.givenName', 'First Name')}</label>
          <input
            type="text"
            id="settings-givenName"
            value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            placeholder={t('settings.givenNamePlaceholder', 'Enter your first name')}
          />
        </div>

        {/* Family Name (Last Name) */}
        <div className="form-group">
          <label htmlFor="settings-familyName">{t('settings.familyName', 'Last Name')}</label>
          <input
            type="text"
            id="settings-familyName"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder={t('settings.familyNamePlaceholder', 'Enter your last name')}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button type="submit" className="btn-save" disabled={isLoading}>
            {isLoading
              ? t('common.saving', 'Saving...')
              : t('settings.saveChanges', 'Save changes')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsGeneral;
